import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Connection, GetProgramAccountsFilter } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { aprToApy, convert, RAYDIUM_STAKE_PROGRAM_ID } from "@stepfinance/common";
import type { FarmInfoDataMap, UserStakeInfoAccountLayout, UserStakeInfoAccountLayoutV3 } from "@stepfinance/farms";
import { USER_STAKE_INFO_ACCOUNT_LAYOUT, USER_STAKE_INFO_ACCOUNT_LAYOUT_V3_1 } from "@stepfinance/farms";
import type { StakingPosition } from "@stepfinance/staking";
import BigNumber from "bignumber.js";
import { useEffect, useState } from "react";

import { getFilteredProgramAccounts } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useFarms } from "../../contexts/farm";
import type { MarketsContextState } from "../../contexts/market";
import { useMarkets } from "../../contexts/market";

const RAY_STAKING_FARM_ID = "4EwbZo8BZXP5313z5A2H11MRBP15M5n6YxfmkjXESKAW";

const getRaydiumStakingPositions = async (
  connection: Connection,
  publicKey: PublicKey,
  priceInUSD: MarketsContextState["priceInUSD"],
  raydiumFarmInfoMap: FarmInfoDataMap<"Raydium">,
): Promise<StakingPosition[]> => {
  const STAKING_FARM = raydiumFarmInfoMap[RAY_STAKING_FARM_ID];

  if (!STAKING_FARM) {
    return [];
  }

  const stakingAccounts: StakingPosition[] = [];

  try {
    const stakeFilter: GetProgramAccountsFilter[] = [
      {
        memcmp: {
          offset: 40,
          bytes: publicKey.toBase58(),
        },
      },
    ];

    const stakeAccountInfos = await getFilteredProgramAccounts(
      connection,
      new PublicKey(RAYDIUM_STAKE_PROGRAM_ID),
      stakeFilter,
    );

    stakeAccountInfos.forEach((stakeAccountInfo) => {
      let userStakeInfo: UserStakeInfoAccountLayout | UserStakeInfoAccountLayoutV3;
      const { data } = stakeAccountInfo.accountInfo;

      if (data.length === USER_STAKE_INFO_ACCOUNT_LAYOUT.span) {
        userStakeInfo = USER_STAKE_INFO_ACCOUNT_LAYOUT.decode(data);
      } else if (data.length === USER_STAKE_INFO_ACCOUNT_LAYOUT_V3_1.span) {
        userStakeInfo = USER_STAKE_INFO_ACCOUNT_LAYOUT_V3_1.decode(data);
      } else {
        return;
      }

      if (userStakeInfo.poolId.toBase58() !== RAY_STAKING_FARM_ID) {
        return;
      }

      const depositBalance = new BigNumber(userStakeInfo.depositBalance.toString());
      const rewardDebt = new BigNumber(userStakeInfo.rewardDebt.toString());
      if (depositBalance.lte(0)) {
        return;
      }

      const farmLP = STAKING_FARM.lpMint;
      const lpMint = STAKING_FARM.raydiumFarm.lpMint;
      const rewardPerShare = STAKING_FARM.raydiumFarm.rewardInfos[0].rewardPerShare;
      const rewardPriceUSD = priceInUSD(lpMint) ?? 0;

      const pendingReward = convert(depositBalance.times(rewardPerShare).minus(rewardDebt), farmLP.decimals);

      stakingAccounts.push({
        kind: "Raydium",
        link: "https://raydium.io/staking/",
        stakingMint: new PublicKey(lpMint),
        valueInUSD: convert(depositBalance, farmLP.decimals, rewardPriceUSD),
        pendingReward,
        pendingRewardUSD: pendingReward * rewardPriceUSD,
        apy: aprToApy(STAKING_FARM.apr / 100),
        amountStaked: convert(depositBalance, farmLP.decimals),
        rewardMint: new PublicKey(lpMint),
      });
    });
  } catch (err) {
    console.error("getRaydiumStakingPositions Error:", err);
    captureException(err);
  }

  return stakingAccounts;
};

export const useRaydiumStaking = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const { farmInfoData } = useFarms();
  const [raydiumStakingPositions, setRaydiumStakingPositions] = useState<StakingPosition[]>([]);

  useEffect(() => setRaydiumStakingPositions([]), [connected]);

  useEffect(() => {
    if (
      connected &&
      publicKey &&
      marketsInitialised &&
      farmInfoData &&
      Object.entries(farmInfoData.raydiumFarms).length > 0
    ) {
      void getRaydiumStakingPositions(connection, publicKey, priceInUSD, farmInfoData.raydiumFarms).then(
        setRaydiumStakingPositions,
      );
    }
  }, [connected, connection, marketsInitialised, priceInUSD, publicKey, farmInfoData]);
  return raydiumStakingPositions;
};
